import React, { useContext, useState } from 'react'
import popupStyles from "./../../assets/styles/componentStyles/globalPopupStyles.module.scss";
import globalStyles from "./../../assets/styles/global.module.scss";
import { Divider, Grid, Typography } from '@mui/material';
import { updateUserDetail } from '../../models/axiosModel';
import { UserContext, UserContextPayload } from '../../context/userContext';
import GlobalButton from './globalButton/button';
import EditUserDetailPopup from './editUserDetailPopup';
import ViewUserDetailPopup from './viewUserDetailPopup';
import UserPasswordForm from './userPasswordForm';
import EditUserTimezonePopup from './editUserTimezonePopup';
import { formatPhoneNumber } from '../../pages/ruleBasedInbox/utils';

type PasswordFormMode = 'set' | 'update' | null;

const UserDetailsPopup = () => {
    const { 
        userDetail,
        flagViewUserDetailPopup,
        setFlagUpdateUserDetailPopup,
    } = useContext(UserContext) as UserContextPayload;

    const innerHeight = window.innerHeight - 150;

    const [flagEditableFields, setFlagEditableFields] = useState<boolean>(false);
    const [passwordFormMode, setPasswordFormMode] = useState<PasswordFormMode>(null);
    const [flagEditTimezone, setFlagEditTimezone] = useState<boolean>(false);
    const [payload, setPayload] = useState<updateUserDetail>({
        user_first_name: userDetail?.user_first_name ?? "",
        user_last_name: userDetail?.user_last_name ?? "",
        user_contact_number: userDetail?.user_phone_number ?? "",
    });

    const getPopupTitle = () => {
        if (passwordFormMode === 'set') {
            return "Set Password";
        }
        if (passwordFormMode === 'update') {
            return "Update Password";
        }
        if (flagEditTimezone) {
            return "Update Timezone";
        }
        return flagViewUserDetailPopup ? "User Profile" : "Complete Your Profile";
    };

    const onClickOfCancel = () => {
        if (flagViewUserDetailPopup) {
            setFlagEditableFields(false);
            setPayload({
                user_first_name: userDetail?.user_first_name ?? "",
                user_last_name: userDetail?.user_last_name ?? "",
                user_contact_number: userDetail?.user_phone_number ?? "",
            });
        } else {
            setFlagUpdateUserDetailPopup(false);
        }
    };

    const onOpenPasswordForm = () => {
        setPasswordFormMode(userDetail?.flag_password ? 'update' : 'set');
    };

    const onCancelPasswordForm = () => {
        setPasswordFormMode(null);
    };

    const onOpenTimezoneForm = () => {
        setFlagEditTimezone(true);
    };

    const onCancelTimezoneForm = () => {
        setFlagEditTimezone(false);
    };

    const renderButton = (
        text: string,
        onClick: () => void,
        className: string = 'primaryButtonStyle',
        width: string,
        disabled: boolean = false
    ) => (
        <Grid item>
            <GlobalButton
                buttonText={text}
                className={className}
                sx={{ width }}
                disabled={disabled}
                onClick={onClick}
            />
        </Grid>
    );

    const userDetailsFields = [
        ...(flagViewUserDetailPopup
        ? [
              {
                  key: "user_email",
                  label: "Email Address",
                  value: userDetail?.user_email,
                  placeholder: "Enter your email address",
                  isDisabled: true,
                  isRequired: true,
              },
          ]
        : []),
        {
            key: "user_first_name",
            label: "First Name",
            value: payload?.user_first_name,
            placeholder: "Enter your first name",
            isDisabled: false,
            isRequired: true,
        },
        {
            key: "user_last_name",
            label: "Last Name",
            value: payload?.user_last_name,
            placeholder: "Enter your last name",
            isDisabled: false,
            isRequired: false,
        },
        {
            key: "user_contact_number",
            label: "Contact Number",
            value: formatPhoneNumber(payload?.user_contact_number),
            placeholder: "Enter your contact number",
            isDisabled: false,
            isRequired: false,
        }
    ]

    return (
        <Grid container>
            <Grid item xs={12}>
                <Grid container className={`${globalStyles.flexColumn} ${globalStyles.justifyCenter}`}>
                    <Grid item className={globalStyles.textCenter}>
                        <Typography className={popupStyles.popupTitleTypography}>
                            {getPopupTitle()}
                        </Typography>
                    </Grid>

                    <Divider className={popupStyles.popupTitleDivider} />

                    <Grid item className={globalStyles.fullwidth}>
                        <Grid container className={`${popupStyles.popupBodyGridContainer} ${globalStyles.overflowAuto}`} gap={2} sx={{ maxHeight: `${innerHeight}px !important` }}>
                            {passwordFormMode ? (
                                <UserPasswordForm
                                    mode={passwordFormMode}
                                    renderButton={renderButton}
                                    onCancel={onCancelPasswordForm}
                                />
                            ) : flagEditTimezone ? (
                                <EditUserTimezonePopup
                                    renderButton={renderButton}
                                    onCancel={onCancelTimezoneForm}
                                />
                            ) : (
                                <>
                                    {!flagViewUserDetailPopup && (
                                        <Grid item mb={2}>
                                            <Typography className={`${globalStyles.primaryFontSize} ${globalStyles.secondaryFontWeight}`}>
                                                Your profile is incomplete. Please add your first name, last name, and contact number to proceed.
                                            </Typography>
                                        </Grid>
                                    )}
                                    
                                    {(!flagViewUserDetailPopup || flagEditableFields) ?
                                        <EditUserDetailPopup
                                            payload={payload}
                                            setPayload={setPayload}
                                            renderButton={renderButton}
                                            onClickOfCancel={onClickOfCancel} 
                                            userDetailsFields={userDetailsFields}
                                            onOpenPasswordForm={onOpenPasswordForm}
                                            onOpenTimezoneForm={onOpenTimezoneForm}
                                        />
                                        :
                                        <ViewUserDetailPopup
                                            userDetailsFields={userDetailsFields}
                                            setFlagEditableFields={setFlagEditableFields}
                                            onOpenPasswordForm={onOpenPasswordForm}
                                            onOpenTimezoneForm={onOpenTimezoneForm}
                                        />
                                    }
                                </>
                            )}
                        </Grid>
                    </Grid>  
                </Grid>
            </Grid>
        </Grid>
    );
};

export default UserDetailsPopup;
